import { match } from '@formatjs/intl-localematcher';
import Negotiator from 'negotiator';
import { NextRequest } from 'next/server';
import { TenantsConfig } from '@/src/lib/store/tenants-config.ts';
import { TENANT } from '@/src/lib/core/config';

if (!TENANT) {
  throw new Error('Tenant is not defined in the environment variables');
}

export const defaultLocale = TenantsConfig[TENANT].defaultLanguage;
export const locales = TenantsConfig[TENANT].locales;
export const languageCookieName = 'lang';
export const clientLocaleCookieName = 'client_locale';

function getLanguageFromReferrer(request: NextRequest) {
  if (!request.headers.get('referer')) {
    return undefined;
  }
  const refererPathname = new URL(String(request.headers.get('referer'))).pathname;
  return locales.find((lang) => refererPathname.startsWith(`/${lang}/`) || refererPathname === `/${lang}`);
}
function getLanguageFromRequestCookie(request: NextRequest) {
  return request.cookies.get(languageCookieName)?.value;
}

export function getClientLocalesFromAcceptLanguageHeader(request: NextRequest) {
  const locales = request.headers.get('accept-language') || defaultLocale;
  return new Negotiator({
    headers: { 'accept-language': locales },
  }).languages()[0];
}

export function getMatchingAppLocaleFromRequest(request: NextRequest) {
  const { pathname } = request.nextUrl;
  const foundLanguages = [];
  const languageFromPathname = locales.find(
    (lang) =>
      pathname.startsWith(`/${TENANT.toLowerCase()}/${lang}/`) || pathname === `/${TENANT.toLowerCase()}/${lang}`,
  );
  const languageFromCookie = getLanguageFromRequestCookie(request);
  const languageFromReferrer = getLanguageFromReferrer(request);
  const languagesFromRequestHeaders = getClientLocalesFromAcceptLanguageHeader(request);

  if (languageFromReferrer) {
    foundLanguages.unshift(languageFromReferrer);
  }
  if (languagesFromRequestHeaders) {
    foundLanguages.unshift(languagesFromRequestHeaders);
  }
  if (languageFromCookie) {
    foundLanguages.unshift(languageFromCookie);
  }
  if (languageFromPathname) {
    foundLanguages.unshift(languageFromPathname);
  }

  return match(
    foundLanguages.filter((locale) => locale !== '*'),
    locales,
    defaultLocale,
  );
}

export function getMatchingAppLocaleFromResponse(response: Response) {
  const lang = (response.headers.get('Content-Language') || 'en').replace('_', '-');

  return match([lang], locales, defaultLocale);
}
